import { Upload, UploadFile } from "antd";
import { AxiosResponse } from "axios";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { IApiErrorResponse } from "../../../../../../../@types/ApiError";
import { Button } from "../../../../../../../components/Button";
import { useDynamicsurveys } from "../../../../../../../hooks/useDynamicSurveys";
import { surveysService } from "../../../../../../../services/surveysService";
import { detectBlur } from "../../../../../../../utils/blurDetection";
import { mixpanelActions } from "../../../../../../../utils/mixpanel";
import {
  parsePaths,
  serializePaths,
} from "../../../../../../../utils/surveyMediaAnswer";
import {
  buttonCss,
  Secondarybutton,
  uploadCss,
} from "./AddMediaButtons.styles";

export interface IPendingUpload {
  id: string;
  name: string;
  percent: number;
}

export interface IButtonProps {
  isLastQuestion: boolean;
  handleNextPageClick: () => void;
  handleSubmit: () => Promise<void>;
  questionId: string;
  userSurveyId?: string;
  surveyAnswerId?: string;
  patientToken?: string;
  isMandatory: boolean;
  setUploadErrorMessage: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  markPathAsBlurry: (path: string) => void;
  pendingUploads: IPendingUpload[];
  setPendingUploads: React.Dispatch<React.SetStateAction<IPendingUpload[]>>;
  setMediaBlobUrls: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
}

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;
const MAX_FILES_PER_QUESTION = 5;

const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/svg+xml",
  "image/heic",
  "image/heif",
]);
const ALLOWED_VIDEO_TYPES = new Set(["video/mp4", "video/quicktime"]);

const ACCEPT_ATTR = [
  ...Array.from(ALLOWED_IMAGE_TYPES),
  ...Array.from(ALLOWED_VIDEO_TYPES),
  ".heic",
  ".heif",
].join(",");

const VIDEO_SIZE_FALLBACK =
  "Videos must be 100 MB or less. Please pick a shorter clip.";
const IMAGE_SIZE_FALLBACK = "Images must be 10 MB or less.";
const UNSUPPORTED_TYPE_FALLBACK =
  "This file type isn't supported. Use JPEG, PNG, WEBP, HEIC, MP4 or MOV.";
const MAX_FILES_FALLBACK = `You can attach up to ${MAX_FILES_PER_QUESTION} files per question.`;

const isImage = (type: string) => ALLOWED_IMAGE_TYPES.has(type);
const isVideo = (type: string) => ALLOWED_VIDEO_TYPES.has(type);

export function AddMediaButtons({
  isLastQuestion,
  isMandatory,
  questionId,
  userSurveyId,
  surveyAnswerId,
  patientToken,
  handleNextPageClick,
  handleSubmit,
  setUploadErrorMessage,
  markPathAsBlurry,
  pendingUploads,
  setPendingUploads,
  setMediaBlobUrls,
}: IButtonProps) {
  const { surveyAnswerArray, updateSurveyAnswerArray } = useDynamicsurveys();
  const { t } = useTranslation();

  const storedAnswer = surveyAnswerArray.find(
    (surveyAnswer) => surveyAnswer.surveyQuestionId === questionId
  )?.answerValue;

  const attachedCount = parsePaths(storedAnswer).length;
  const hasAttached = attachedCount > 0;
  const isAnythingUploading = pendingUploads.length > 0;
  const isCapReached =
    attachedCount + pendingUploads.length >= MAX_FILES_PER_QUESTION;
  const isAttachDisabled = isAnythingUploading || isCapReached;

  // Concurrent uploads each resolve in their own .then closure; reading
  // surveyAnswerArray from those closures gives a stale snapshot, so each
  // append clobbers the others. Hold the committed paths in a ref so every
  // append sees the same up-to-date list, regardless of which closure runs.
  const pathsRef = useRef<string[]>(parsePaths(storedAnswer));
  useEffect(() => {
    pathsRef.current = parsePaths(storedAnswer);
  }, [questionId, storedAnswer]);

  // Counts uploads we've accepted but not yet finalised. The OS picker
  // fires onChange once per file synchronously, so React state hasn't
  // re-rendered between those callbacks - we need a ref to enforce the
  // 5-file cap atomically across the burst.
  const slotsInFlightRef = useRef(0);

  const translatedOrFallback = (key: string, fallback: string) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const primaryButtonLabel = isLastQuestion
    ? `${t("survey_fixed_translations_survey_navigation_submit")}`
    : `${t("survey_fixed_translations_survey_navigation_next")}`;

  const attachButtonLabel = hasAttached
    ? translatedOrFallback(
        "survey_fixed_translations_media_question_attach_more",
        "Attach more"
      )
    : translatedOrFallback(
        "survey_fixed_translations_media_question_attach",
        "Attach"
      );

  const GENERIC_API_ERROR_FALLBACK =
    "We couldn't upload that file. Please try again.";
  const NO_RESPONSE_FALLBACK =
    "We couldn't reach the server. Check your connection and try again.";

  const reportNetworkError = () => {
    if (!navigator.onLine) {
      setUploadErrorMessage(
        `${t("survey_fixed_translations_media_question_error_network")}`
      );
      mixpanelActions.track("Error view: Internet connection required");
      return;
    }
    setUploadErrorMessage(
      translatedOrFallback(
        "survey_fixed_translations_media_question_error_no_response",
        NO_RESPONSE_FALLBACK
      )
    );
    mixpanelActions.track("Error view: Upload no response");
  };

  const extractApiErrorMessage = (
    response: AxiosResponse | undefined
  ): string | undefined => {
    const body = response?.data as IApiErrorResponse | undefined;
    return body?.errors?.[0]?.message;
  };

  const reportApiError = (response: AxiosResponse) => {
    const apiMessage = extractApiErrorMessage(response);
    setUploadErrorMessage(
      apiMessage ??
        translatedOrFallback(
          "survey_fixed_translations_media_question_error_api",
          GENERIC_API_ERROR_FALLBACK
        )
    );
    mixpanelActions.track("Error view: Upload API rejected", {
      status: response.status,
      code: (response.data as IApiErrorResponse | undefined)?.errors?.[0]?.code,
    });
  };

  const validateFile = (file: File): string | undefined => {
    if (!isImage(file.type) && !isVideo(file.type)) {
      return translatedOrFallback(
        "survey_fixed_translations_media_question_error_unsupported_type",
        UNSUPPORTED_TYPE_FALLBACK
      );
    }
    if (isImage(file.type) && file.size > MAX_IMAGE_SIZE_BYTES) {
      return translatedOrFallback(
        "survey_fixed_translations_media_question_error_size",
        IMAGE_SIZE_FALLBACK
      );
    }
    if (isVideo(file.type) && file.size > MAX_VIDEO_SIZE_BYTES) {
      return translatedOrFallback(
        "survey_fixed_translations_media_question_error_video_size",
        VIDEO_SIZE_FALLBACK
      );
    }
    return undefined;
  };

  // Returns a promise that resolves to true if the image looks blurry.
  // Non-image files always resolve to false. Errors are swallowed and
  // surface as false too — blur detection is best-effort and must not
  // block the upload flow.
  const runBlurDetection = (file: File): Promise<boolean> => {
    if (!isImage(file.type)) return Promise.resolve(false);
    return detectBlur(file)
      .then(({ variance, isBlurry }) => {
        console.log(
          "[blur] variance=%s isBlurry=%s",
          variance.toFixed(2),
          isBlurry
        );
        return isBlurry;
      })
      .catch(() => false);
  };

  const appendPathToAnswer = (path: string) => {
    pathsRef.current = [...pathsRef.current, path];
    updateSurveyAnswerArray({
      surveyQuestionId: questionId,
      answerValue: serializePaths(pathsRef.current),
    });
  };

  const uploadFile = async (file: File) => {
    if (!questionId || (!userSurveyId && !surveyAnswerId)) return;

    const usedSlots = pathsRef.current.length + slotsInFlightRef.current;
    if (usedSlots >= MAX_FILES_PER_QUESTION) {
      setUploadErrorMessage(
        translatedOrFallback(
          "survey_fixed_translations_media_question_error_max_files",
          MAX_FILES_FALLBACK
        )
      );
      mixpanelActions.track("Error view: Media file cap reached");
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setUploadErrorMessage(validationError);
      mixpanelActions.track("Error view: Media validation failed");
      return;
    }

    setUploadErrorMessage(undefined);
    slotsInFlightRef.current += 1;
    // Kick off blur detection in parallel with the upload so the warning
    // surfaces almost as soon as the upload completes.
    const blurPromise = runBlurDetection(file);

    const uploadId = `${file.name}-${Date.now()}-${Math.random()}`;
    setPendingUploads((prev) => [
      ...prev,
      { id: uploadId, name: file.name, percent: 0 },
    ]);
    const localUrl = URL.createObjectURL(file);

    try {
      const result = await surveysService.uploadSurveyAnswerMedia({
        surveyQuestionId: questionId,
        file,
        userSurveyId,
        surveyAnswerId,
        patientToken,
        onProgress: (percent) => {
          setPendingUploads((prev) =>
            prev.map((p) => (p.id === uploadId ? { ...p, percent } : p))
          );
        },
      });

      if (result.ok) {
        appendPathToAnswer(result.key);
        setMediaBlobUrls((prev) => ({ ...prev, [result.key]: localUrl }));
        blurPromise.then((isBlurry) => {
          if (isBlurry) markPathAsBlurry(result.key);
        });
      } else {
        URL.revokeObjectURL(localUrl);
        // signed-url and confirm failures carry an axios response (or
        // undefined when no response was received). The s3-put stage
        // never has a response in this shape — it's always a network
        // class failure from the client's view of S3.
        if (result.stage === "s3-put") {
          reportNetworkError();
        } else if (result.response) {
          reportApiError(result.response);
        } else {
          reportNetworkError();
        }
      }
    } catch {
      // Belt-and-braces: uploadSurveyAnswerMedia is designed not to throw,
      // but if anything slips through we still surface a usable error.
      URL.revokeObjectURL(localUrl);
      reportNetworkError();
    } finally {
      setPendingUploads((prev) => prev.filter((p) => p.id !== uploadId));
      slotsInFlightRef.current -= 1;
    }
  };

  const handleFileChange = (info: {
    file: UploadFile;
    fileList: UploadFile[];
  }) => {
    const file = info.file as unknown as File;
    if (file) {
      uploadFile(file);
    }
  };

  const handlePrimaryClick = () => {
    if (isLastQuestion) {
      handleSubmit();
      return;
    }
    handleNextPageClick();
  };

  const isPrimaryDisabled =
    isAnythingUploading || (isMandatory && !hasAttached);

  return (
    <>
      <Button
        type="button"
        label={primaryButtonLabel}
        onClick={handlePrimaryClick}
        disabled={isPrimaryDisabled}
        className={buttonCss()}
      />
      <Upload
        accept={ACCEPT_ATTR}
        multiple
        showUploadList={false}
        beforeUpload={() => false}
        onChange={handleFileChange}
        className={uploadCss()}
        disabled={isAttachDisabled}
      >
        <Secondarybutton type="button" disabled={isAttachDisabled}>
          {attachButtonLabel}
        </Secondarybutton>
      </Upload>
      {!isMandatory && (
        <Secondarybutton
          disabled={isAnythingUploading}
          onClick={!isAnythingUploading ? handleNextPageClick : undefined}
        >
          {`${t("survey_fixed_translations_survey_navigation_skip")}`}
        </Secondarybutton>
      )}
    </>
  );
}
